import { useNavigate, useParams } from 'react-router-dom';
import { BasicLayout } from '../../../shared/layout';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../shared/infra/redux';
import { getCommunityProfile } from '../../../modules/users/redux/operators';
import ProfileIcon from '../../../shared/components/topNav/components/ProfileIcon';
import { LoadingModal } from '../../../shared/components/loader';
import DeleteUserTemplate from '../../../modules/users/components/deleteUser/deleteUserTemplate';
import requestDeleteUser from '../../../modules/users/redux/operators/requestDeleteUser';
import { DeleteUserRequest } from '../../../modules/users/infra/types/DeleteUserRequest.type';
import { useNotificationCenter } from '../../../shared/components/toast';
import { GotMailTemplate } from '../../../modules/users/components';

type DeleteUserPageProps = {
  community?: string;
};

export default function DeleteUserPage(): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.public.profile);
  const { community } = useParams<DeleteUserPageProps>();
  const { addNotification } = useNotificationCenter();
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);

  const handleFetchProfile = useCallback(async () => {
    const resultAction = await dispatch(
      getCommunityProfile({ slug: community }),
    );
    if (getCommunityProfile.rejected.match(resultAction)) {
      console.log(resultAction);
      navigate('/', { replace: true });
    }

    setIsLoading(false);
  }, [community, dispatch, navigate]);

  const onSubmitDeleteRequest = useCallback(
    async (request: DeleteUserRequest) => {
      const resultAction = await dispatch(requestDeleteUser(request));
      if (requestDeleteUser.rejected.match(resultAction)) {
        addNotification(
          {
            title: 'Error',
            description: resultAction.payload
              ? resultAction.payload.message
              : 'Invalid Email',
          },
          {
            variant: 'ERROR',
          },
        );
      }

      if (requestDeleteUser.fulfilled.match(resultAction)) {
        setIsSuccessful(true);
      }
    },
    [addNotification, dispatch],
  );

  const buildHeaderView = (): React.ReactNode | undefined => {
    if (!profile) return;

    return (
      <div className='flex flex-row items-center space-x-2'>
        <ProfileIcon
          imgSrc={profile.logo ? profile.logo : undefined}
          className='flex-shrink-0'
        />
        <h1 className='text-xs font-bold uppercase md:text-lg'>
          {profile.name}
        </h1>
      </div>
    );
  };

  const handleOnBackClick = () => {
    setIsSuccessful(false);
  };

  useEffect(() => {
    if (community) {
      setIsLoading(true);
      void handleFetchProfile();
    }
  }, [community, handleFetchProfile]);

  return isLoading ? (
    <LoadingModal isLoading={true} />
  ) : (
    <BasicLayout withPrivacy={false} withLogo={profile && buildHeaderView()}>
      <div className='mx-auto mt-16 flex w-2/3 flex-1 items-center justify-center lg:w-1/2 xl:w-1/3'>
        {isSuccessful ? (
          <GotMailTemplate
            onBack={handleOnBackClick}
            subtitle='Please check your inbox to complete your user deletion'
            withBackNav={false}
          />
        ) : (
          <DeleteUserTemplate
            profile={profile}
            onSubmit={onSubmitDeleteRequest}
          />
        )}
      </div>
    </BasicLayout>
  );
}
